import { useMutation, useQuery } from 'react-query';
import request from './request';
import { QUERY_RETRY, DATA_QUERY_EXPIRATION } from './queries';

export interface KpiSource {
  value: string;
  label: string;
}

export interface KpiMetric {
  value: string;
  label: string;
}

export interface KpiCategory {
  value: string;
  label: string;
}

export interface KpiDataConfig {
  sources: KpiSource[];
  metrics: KpiMetric[];
  categories: KpiCategory[];
}

export type KpiMetricValue = 'sales' | 'units_sold' | 'customers_registrations';

export interface UserModel {
  modelName: string;
  kpiSource: 'uploaded_file';
  kpiMetric: KpiMetricValue;
  kpiCategory: string | null;
  selectedMedia: string[];
  campaignSelections: Record<string, Record<string, boolean>>;
}

export interface StartModelingRequest {
  customer: string;
  models: UserModel[];
}

class KpiDataService {
  async getSources(customer: string): Promise<KpiSource[]> {
    const response = await request.get('/data/kpi-sources', {
      params: { customer },
    });
    return response.data;
  }

  async getMetrics(customer: string, sourceId: string): Promise<KpiMetric[]> {
    const response = await request.get('/data/kpi-metrics', {
      params: { customer, source: sourceId },
    });
    return response.data;
  }

  async getCategories(
    customer: string,
    sourceId: string,
  ): Promise<KpiCategory[]> {
    const response = await request.get('/data/kpi-categories', {
      params: { customer, source: sourceId },
    });
    return response.data;
  }
}

export const kpiDataService = new KpiDataService();

export function useStartModelingMutation() {
  return useMutation((data: StartModelingRequest) => {
    const { customer, models } = data;
    return request.post('/data/models', { models }, { params: { customer } });
  });
}

export function useKpiSourcesQuery(customer: string) {
  return useQuery(
    ['kpiSources', customer],
    () => kpiDataService.getSources(customer),
    {
      enabled: Boolean(customer),
      retry: QUERY_RETRY,
      staleTime: DATA_QUERY_EXPIRATION,
    },
  );
}
